import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import nexusApi from '../../services/nexusApi'
import {
  Search, Building2, Users, ChevronRight, Plus, Filter,
  CheckCircle, Clock, XCircle, AlertTriangle
} from 'lucide-react'

const NX = {
  bg: '#0D1117', surface: '#161B22', surface2: '#1C2330', border: '#2A3441',
  text: '#E6EDF3', textDim: '#8B949E', accent: '#0066FF',
  green: '#3DB54A', amber: '#F5921E', red: '#EF4444',
}

const FILTERS = ['all', 'active', 'trial', 'past_due', 'cancelled']

function PlanBadge({ plan }: { plan: string }) {
  const colors: Record<string, string> = { trial: NX.amber, standard: NX.accent, professional: NX.green, enterprise: '#A855F7' }
  return (
    <span style={{
      padding: '0.2rem 0.5rem', borderRadius: 4, fontSize: '0.6875rem', fontWeight: 700,
      background: `${colors[plan] || NX.textDim}20`, color: colors[plan] || NX.textDim,
      textTransform: 'capitalize',
    }}>{plan}</span>
  )
}

interface Props { usersMode?: boolean }

export default function NexusTenants({ usersMode = false }: Props) {
  const [tenants, setTenants] = useState<any[]>([])
  const [users, setUsers] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pages, setPages] = useState(1)
  const [filter, setFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState('')
  const [form, setForm] = useState({ name: '', adminEmail: '', adminName: '', plan: 'trial', industry: '' })
  const [createResult, setCreateResult] = useState<any>(null)

  useEffect(() => {
    if (usersMode) {
      loadUsers()
    } else {
      loadTenants()
    }
  }, [filter, search, page, usersMode])

  async function loadTenants() {
    setLoading(true)
    try {
      const { data } = await nexusApi.get('/tenants', { params: { filter, search, page, limit: 20 } })
      setTenants(data.tenants)
      setTotal(data.total)
      setPages(data.pages)
    } catch (e) { console.error(e) }
    finally { setLoading(false) }
  }

  async function loadUsers() {
    setLoading(true)
    try {
      const { data } = await nexusApi.get('/users', { params: { search, page, limit: 20 } })
      setUsers(data.users)
      setTotal(data.total)
      setPages(data.pages)
    } catch (e) { console.error(e) }
    finally { setLoading(false) }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    setCreating(true)
    setCreateError('')
    try {
      const { data } = await nexusApi.post('/tenants', form)
      setCreateResult(data)
      loadTenants()
    } catch (err: any) {
      setCreateError(err.response?.data?.error || 'Failed to create tenant')
    } finally {
      setCreating(false)
    }
  }

  const inputStyle = {
    background: NX.surface2, border: `1px solid ${NX.border}`, borderRadius: 8,
    padding: '0.5rem 0.875rem', color: NX.text, fontSize: '0.875rem',
    outline: 'none', width: '100%', boxSizing: 'border-box' as const,
  }

  return (
    <div style={{ padding: '2rem', background: NX.bg, minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ color: NX.text, fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>
            {usersMode ? 'All Users' : 'Tenants'}
          </h1>
          <p style={{ color: NX.textDim, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>{total} total</p>
        </div>
        {!usersMode && (
          <button onClick={() => { setShowCreate(true); setCreateResult(null) }} style={{
            background: NX.accent, color: '#fff', border: 'none', borderRadius: 8,
            padding: '0.5rem 1rem', fontSize: '0.875rem', fontWeight: 600,
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.375rem',
          }}>
            <Plus size={15} /> New Tenant
          </button>
        )}
      </div>

      {/* Filters + Search */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        {!usersMode && FILTERS.map(f => (
          <button key={f} onClick={() => { setFilter(f); setPage(1) }} style={{
            background: filter === f ? NX.accent : NX.surface,
            color: filter === f ? '#fff' : NX.textDim,
            border: `1px solid ${filter === f ? NX.accent : NX.border}`,
            borderRadius: 6, padding: '0.375rem 0.75rem',
            fontSize: '0.8125rem', fontWeight: 500, cursor: 'pointer', textTransform: 'capitalize',
          }}>{f.replace('_', ' ')}</button>
        ))}
        <div style={{ display: 'flex', gap: '0.5rem', marginLeft: 'auto' }}>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: NX.textDim }} />
            <input
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { setSearch(searchInput); setPage(1) } }}
              placeholder={usersMode ? 'Search users...' : 'Search tenants...'}
              style={{ ...inputStyle, paddingLeft: '2rem', width: 220 }}
            />
          </div>
          <button onClick={() => { setSearch(searchInput); setPage(1) }} style={{
            background: NX.surface, border: `1px solid ${NX.border}`, borderRadius: 8,
            padding: '0.5rem 0.875rem', color: NX.text, cursor: 'pointer', fontSize: '0.875rem',
          }}>Search</button>
        </div>
      </div>

      {/* Table */}
      <div style={{ background: NX.surface, border: `1px solid ${NX.border}`, borderRadius: 12, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: NX.textDim }}>Loading...</div>
        ) : usersMode ? (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${NX.border}` }}>
                {['User', 'Tenant', 'Role', 'Last Login', 'Status', 'Flags'].map(h => (
                  <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', color: NX.textDim, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={u.id} style={{ borderBottom: i < users.length - 1 ? `1px solid ${NX.border}` : 'none' }}>
                  <td style={{ padding: '0.875rem 1rem' }}>
                    <div style={{ color: NX.text, fontSize: '0.875rem', fontWeight: 500 }}>{u.first_name} {u.last_name}</div>
                    <div style={{ color: NX.textDim, fontSize: '0.75rem' }}>{u.email}</div>
                  </td>
                  <td style={{ padding: '0.875rem 1rem' }}>
                    <Link to={`/nexus/tenants/${u.tenant_id}`} target="_blank" rel="noreferrer" style={{ color: NX.accent, fontSize: '0.875rem', textDecoration: 'none' }}>
                      {u.tenant_name}
                    </Link>
                  </td>
                  <td style={{ padding: '0.875rem 1rem' }}>
                    <span style={{ color: NX.textDim, fontSize: '0.8125rem', textTransform: 'capitalize' }}>{u.role?.replace('_', ' ')}</span>
                  </td>
                  <td style={{ padding: '0.875rem 1rem', color: NX.textDim, fontSize: '0.8125rem' }}>
                    {u.last_login ? new Date(u.last_login).toLocaleDateString('en-AU') : 'Never'}
                  </td>
                  <td style={{ padding: '0.875rem 1rem' }}>
                    <span style={{ color: u.active ? NX.green : NX.red, fontSize: '0.8125rem' }}>
                      {u.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '0.875rem 1rem' }}>
                    <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
                      <span title={u.welcome_email_sent ? 'Welcome email sent' : 'Email not sent'} style={{ fontSize: '0.6875rem', fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: u.welcome_email_sent ? 'rgba(61,181,74,0.15)' : 'rgba(239,68,68,0.12)', color: u.welcome_email_sent ? NX.green : NX.red }}>
                        {u.welcome_email_sent ? '✉ Sent' : '✉ Unsent'}
                      </span>
                      {u.first_login_at ? (
                        <span title={`First login: ${new Date(u.first_login_at).toLocaleDateString('en-AU')}`} style={{ fontSize: '0.6875rem', fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: 'rgba(61,181,74,0.15)', color: NX.green }}>1st ✓</span>
                      ) : (
                        <span title="Never logged in" style={{ fontSize: '0.6875rem', fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: 'rgba(245,146,30,0.15)', color: NX.amber }}>1st –</span>
                      )}
                      {u.password_changed_at ? (
                        <span title={`Password changed: ${new Date(u.password_changed_at).toLocaleDateString('en-AU')}`} style={{ fontSize: '0.6875rem', fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: 'rgba(61,181,74,0.15)', color: NX.green }}>PW ✓</span>
                      ) : (
                        <span title="Using temp password" style={{ fontSize: '0.6875rem', fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: u.must_change_password ? 'rgba(239,68,68,0.12)' : 'rgba(139,148,158,0.15)', color: u.must_change_password ? NX.red : NX.textDim }}>PW {u.must_change_password ? '!' : '–'}</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${NX.border}` }}>
                {['Tenant', 'Plan', 'Users', 'Incidents', 'Tickets', 'Created', ''].map(h => (
                  <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', color: NX.textDim, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tenants.map((t, i) => (
                <tr key={t.id}
                  onClick={() => window.open(`/nexus/tenants/${t.id}`, '_blank')}
                  style={{ borderBottom: i < tenants.length - 1 ? `1px solid ${NX.border}` : 'none', cursor: 'pointer' }}
                  onMouseEnter={e => (e.currentTarget.style.background = NX.surface2)}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <td style={{ padding: '0.875rem 1rem' }}>
                    <div style={{ color: NX.text, fontSize: '0.875rem', fontWeight: 600 }}>{t.name}</div>
                    <div style={{ color: NX.textDim, fontSize: '0.75rem' }}>{t.industry || 'N/A'} · {t.abn || 'No ABN'}</div>
                  </td>
                  <td style={{ padding: '0.875rem 1rem' }}>
                    <PlanBadge plan={t.plan} />
                    {t.plan === 'trial' && t.plan_expires_at && (
                      <div style={{ color: NX.textDim, fontSize: '0.6875rem', marginTop: 3 }}>
                        Exp: {new Date(t.plan_expires_at).toLocaleDateString('en-AU')}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '0.875rem 1rem', color: NX.text, fontSize: '0.875rem' }}>{t.user_count}</td>
                  <td style={{ padding: '0.875rem 1rem', color: NX.text, fontSize: '0.875rem' }}>{t.incident_count}</td>
                  <td style={{ padding: '0.875rem 1rem' }}>
                    {t.open_tickets > 0 ? (
                      <span style={{ color: NX.amber, fontSize: '0.8125rem', fontWeight: 600 }}>{t.open_tickets}</span>
                    ) : (
                      <span style={{ color: NX.textDim, fontSize: '0.8125rem' }}>0</span>
                    )}
                  </td>
                  <td style={{ padding: '0.875rem 1rem', color: NX.textDim, fontSize: '0.8125rem' }}>
                    {new Date(t.created_at).toLocaleDateString('en-AU')}
                  </td>
                  <td style={{ padding: '0.875rem 1rem' }} onClick={e => e.stopPropagation()}>
                    <Link to={`/nexus/tenants/${t.id}`} target="_blank" rel="noreferrer" style={{
                      color: NX.accent, fontSize: '0.8125rem', textDecoration: 'none',
                      display: 'flex', alignItems: 'center', gap: 2,
                    }}>
                      View <ChevronRight size={12} />
                    </Link>
                  </td>
                </tr>
              ))}
              {tenants.length === 0 && (
                <tr><td colSpan={7} style={{ padding: '3rem', textAlign: 'center', color: NX.textDim }}>No tenants found</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', marginTop: '1.25rem' }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ background: NX.surface, border: `1px solid ${NX.border}`, color: NX.text, borderRadius: 6, padding: '0.375rem 0.75rem', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.5 : 1 }}>Prev</button>
          <span style={{ color: NX.textDim, fontSize: '0.875rem', padding: '0.375rem 0.75rem' }}>Page {page} of {pages}</span>
          <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages} style={{ background: NX.surface, border: `1px solid ${NX.border}`, color: NX.text, borderRadius: 6, padding: '0.375rem 0.75rem', cursor: page === pages ? 'not-allowed' : 'pointer', opacity: page === pages ? 0.5 : 1 }}>Next</button>
        </div>
      )}

      {/* Create Tenant Modal */}
      {showCreate && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem',
        }} onClick={e => { if (e.target === e.currentTarget) { setShowCreate(false) } }}>
          <div style={{ background: NX.surface, border: `1px solid ${NX.border}`, borderRadius: 12, padding: '1.75rem', width: '100%', maxWidth: 480 }}>
            <h2 style={{ color: NX.text, fontSize: '1.125rem', fontWeight: 700, margin: '0 0 1.5rem' }}>Create New Tenant</h2>

            {createResult ? (
              <div>
                <div style={{ background: 'rgba(61,181,74,0.1)', border: `1px solid ${NX.green}`, borderRadius: 8, padding: '1rem', marginBottom: '1.25rem' }}>
                  <div style={{ color: NX.green, fontWeight: 600, marginBottom: '0.5rem' }}>Tenant Created Successfully</div>
                  <div style={{ color: NX.textDim, fontSize: '0.875rem' }}>
                    <div><b style={{ color: NX.text }}>Name:</b> {createResult.name}</div>
                    <div><b style={{ color: NX.text }}>Admin:</b> {createResult.adminEmail}</div>
                    {createResult.tempPassword && <div><b style={{ color: NX.text }}>Temp Password:</b> <code style={{ color: NX.amber }}>{createResult.tempPassword}</code></div>}
                  </div>
                </div>
                <button onClick={() => { setShowCreate(false); setForm({ name: '', adminEmail: '', adminName: '', plan: 'trial', industry: '' }) }} style={{ background: NX.accent, color: '#fff', border: 'none', borderRadius: 8, padding: '0.625rem 1.25rem', cursor: 'pointer', fontWeight: 600 }}>Close</button>
              </div>
            ) : (
              <form onSubmit={handleCreate}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
                  <div>
                    <label style={{ color: NX.textDim, fontSize: '0.8125rem', display: 'block', marginBottom: 4 }}>Company Name *</label>
                    <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={inputStyle} placeholder="Acme Corp" />
                  </div>
                  <div>
                    <label style={{ color: NX.textDim, fontSize: '0.8125rem', display: 'block', marginBottom: 4 }}>Admin Email *</label>
                    <input required type="email" value={form.adminEmail} onChange={e => setForm({ ...form, adminEmail: e.target.value })} style={inputStyle} placeholder="admin@company.com.au" />
                  </div>
                  <div>
                    <label style={{ color: NX.textDim, fontSize: '0.8125rem', display: 'block', marginBottom: 4 }}>Admin Name</label>
                    <input value={form.adminName} onChange={e => setForm({ ...form, adminName: e.target.value })} style={inputStyle} placeholder="Jane Smith" />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                    <div>
                      <label style={{ color: NX.textDim, fontSize: '0.8125rem', display: 'block', marginBottom: 4 }}>Plan</label>
                      <select value={form.plan} onChange={e => setForm({ ...form, plan: e.target.value })} style={{ ...inputStyle }}>
                        <option value="trial">Trial</option>
                        <option value="standard">Standard</option>
                        <option value="professional">Professional</option>
                        <option value="enterprise">Enterprise</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ color: NX.textDim, fontSize: '0.8125rem', display: 'block', marginBottom: 4 }}>Industry</label>
                      <input value={form.industry} onChange={e => setForm({ ...form, industry: e.target.value })} style={inputStyle} placeholder="Construction" />
                    </div>
                  </div>
                </div>
                {createError && (
                  <div style={{ marginTop: '1rem', padding: '0.625rem 0.875rem', borderRadius: 8, background: 'rgba(239,68,68,0.1)', border: `1px solid ${NX.red}`, color: NX.red, fontSize: '0.8125rem' }}>
                    {createError}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem', justifyContent: 'flex-end' }}>
                  <button type="button" onClick={() => { setShowCreate(false); setCreateError('') }} style={{ background: NX.surface2, border: `1px solid ${NX.border}`, color: NX.text, borderRadius: 8, padding: '0.625rem 1.25rem', cursor: 'pointer' }}>Cancel</button>
                  <button type="submit" disabled={creating} style={{ background: NX.accent, color: '#fff', border: 'none', borderRadius: 8, padding: '0.625rem 1.25rem', cursor: 'pointer', fontWeight: 600, opacity: creating ? 0.7 : 1 }}>
                    {creating ? 'Creating...' : 'Create Tenant'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
